import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
} from "@mui/material";
import {
  Search as SearchIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import {
  getFullDataNoValidate,
  insertQualityTerm,
} from "../services/ConnectDataBase";
import { translateText } from "../translations/translationService";
import {
  SCREEN_IDS,
  backgroundColor2,
  typeConnect,
} from "../constants/Constants";
import { useSelector } from "react-redux";
import { VALIDAR_DATOS_CALIDAD_COLORS } from "../style/colors.js";
import AppButton from "../components/Common/ui/AppButton";
import AppTextField from "../components/Common/ui/AppTextField";

export default function ValidarPesos() {
  const [isLoading, setIsLoading] = useState(false);
  const [listData, setListData] = useState([]); // Para almacenar los datos
  const [refSearch, setRefSearch] = useState(""); // Para buscar
  const [searchResult, setSearchResult] = useState(null);
  const lang = useSelector((state) => state.lang.value);
  const currentScreenId = SCREEN_IDS.VALIDATE_QUALITY;
  const usuario = useSelector((state) => state.user.name);
  const equipo = useSelector((state) => state.user.team);

  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const listaSinValidar = await getFullDataNoValidate(
        currentScreenId,
        typeConnect[0]
      );
      // console.log(listaSinValidar);

      const usuariosConPermisoCompleto = [
        "Jose Miguel",
        "Javier Rodriguez",
        "Carlos Maldonado",
        "Gabriel Garcia",
      ];

      let filteredData = listaSinValidar;

      const tienePermisoCompleto = usuariosConPermisoCompleto.includes(usuario);

      if (!tienePermisoCompleto) {
        filteredData = listaSinValidar.filter((item) => {
          const deviation = calculateDeviation(item.peso, item.peso_teorico);
          return parseFloat(deviation) <= 15;
        });
      }

      setListData(filteredData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error cargando datos:", error);
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    const filteredData = listData.filter(
      (item) =>
        item.mo.includes(refSearch) || item.referencia.includes(refSearch)
    );
    setSearchResult(filteredData);
  };

  const handleAction = async (item) => {
    try {
      // Validar el peso específico
      await insertQualityTerm(
        currentScreenId,
        typeConnect[1],
        usuario,
        equipo,
        item
      );

      // Actualizar la línea validada en el estado local
      setListData((prevListData) =>
        prevListData.filter((dataItem) => dataItem.mo !== item.mo)
      );

      // Opcional: Si quieres limpiar la búsqueda después de validar
      setSearchResult(
        (prevSearchResult) =>
          prevSearchResult?.filter((dataItem) => dataItem.mo !== item.mo) ||
          null
      );
    } catch (error) {
      console.error("Error al validar el peso:", error);
    }
  };

  const calculateDeviation = (peso, peso_teorico) => {
    const deviation = ((peso - peso_teorico) / peso_teorico) * 100;
    return deviation.toFixed(2);
  };

  const dataToDisplay = searchResult || listData;

  return (
    <Box sx={{ padding: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
        }}>
        <Box sx={{ flex: 1, minWidth: "280px", position: "relative" }}>
          <AppTextField
            value={refSearch}
            onChange={(e) => setRefSearch(e.target.value)}
            placeholder={translateText("Buscar MO o Referencia", lang)}
            sx={{
              "& .MuiOutlinedInput-root": {
                pl: 3.5,
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: VALIDAR_DATOS_CALIDAD_COLORS.TEXT_MUTED || "#9CA3AF",
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        <AppButton
          onClick={handleSearch}
          disabled={isLoading}
          startIcon={<SearchIcon />}
          sx={{ minWidth: 140 }}>
          {translateText("Buscar", lang)}
        </AppButton>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: VALIDAR_DATOS_CALIDAD_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <Typography variant='h6' sx={{ flex: 1, textAlign: "center", fontSize: "1rem", fontWeight: 600 }}>
          {translateText("Validar Pesos", lang)}
        </Typography>

        <IconButton
          onClick={fetchData}
          sx={{
            padding: 1,
            color: VALIDAR_DATOS_CALIDAD_COLORS.TEXT_BRAND || "#174045",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(70, 93, 100, 0.08)",
              transform: "scale(1.1)",
            },
          }}>
          <RestartAltIcon />
        </IconButton>
      </Box>

      {isLoading ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      translateText("MO", lang),
                      translateText("Referencia", lang),
                      translateText("Observaciones", lang),
                      translateText("Cantidad Proceso", lang),
                      translateText("Peso", lang),
                      translateText("Peso Teórico", lang),
                      translateText("Desviación (%)", lang),
                      translateText("Acción", lang),
                    ].map((header, i) => (
                      <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(8)].map((_, j) => (
                        <TableCell key={j} sx={{ py: 0.75, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      ) : dataToDisplay && dataToDisplay.length > 0 ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
            <Table stickyHeader size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: VALIDAR_DATOS_CALIDAD_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("MO", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: VALIDAR_DATOS_CALIDAD_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Referencia", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: VALIDAR_DATOS_CALIDAD_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Observaciones", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: VALIDAR_DATOS_CALIDAD_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Cantidad Proceso", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: VALIDAR_DATOS_CALIDAD_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Peso", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: VALIDAR_DATOS_CALIDAD_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Peso Teórico", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: VALIDAR_DATOS_CALIDAD_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Desviación (%)", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: VALIDAR_DATOS_CALIDAD_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Acción", lang)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataToDisplay.map((item) => (
                  <TableRow
                    key={item.mo}
                    sx={{
                      "&:hover": {
                        backgroundColor: VALIDAR_DATOS_CALIDAD_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
                      },
                      borderBottom: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                    }}>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: VALIDAR_DATOS_CALIDAD_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {item.mo}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: VALIDAR_DATOS_CALIDAD_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {item.referencia}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: VALIDAR_DATOS_CALIDAD_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {item.observaciones}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: VALIDAR_DATOS_CALIDAD_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {parseFloat(item.cantidad_proceso).toFixed(1)}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: VALIDAR_DATOS_CALIDAD_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {parseFloat(item.peso).toFixed(3)}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: VALIDAR_DATOS_CALIDAD_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {parseFloat(item.peso_teorico).toFixed(3)}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: VALIDAR_DATOS_CALIDAD_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {calculateDeviation(item.peso, item.peso_teorico)}%
                    </TableCell>
                    <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                      <AppButton
                        onClick={() => handleAction(item)}
                        disabled={isLoading}
                        sx={{
                          minHeight: 28,
                          px: 1.5,
                          fontSize: "0.75rem",
                        }}>
                        {translateText("Validar", lang)}
                      </AppButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${VALIDAR_DATOS_CALIDAD_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
            textAlign: "center",
            py: 8,
          }}>
          <Typography variant='h6' sx={{ color: VALIDAR_DATOS_CALIDAD_COLORS.TEXT_SECONDARY || "#6B7280" }}>
            {translateText("No hay MOs que autorizar", lang)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
